import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store';
import App from './App';
import './index.css';
import './styles/globals.css';

const CHUNK_RELOAD_STORAGE_KEY = 'aimepic_chunk_reload_once';
const CHUNK_RELOAD_CLEAR_DELAY_MS = 10000;

const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const maybeError = error as {
    name?: string;
    message?: string;
    type?: string;
    filename?: string;
  };
  const text = `${maybeError.name || ''} ${maybeError.message || ''} ${maybeError.type || ''} ${maybeError.filename || ''}`.toLowerCase();

  return (
    maybeError.name === 'ChunkLoadError' ||
    text.includes('loading chunk') ||
    text.includes('chunkloaderror') ||
    (text.includes('chunk') && text.includes('failed')) ||
    text.includes('.chunk.js')
  );
};

const tryRecoverFromChunkLoadError = (error: unknown): boolean => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  const message = error instanceof Error ? error.message : String((error as any)?.message || 'Chunk load error');

  try {
    const hasRetried = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === '1';
    if (hasRetried) {
      console.error('[ChunkRecovery] Chunk load failed after retry:', message);
      return false;
    }

    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, '1');
    const retryUrl = new URL(window.location.href);
    retryUrl.searchParams.set('_chunk_retry', Date.now().toString());
    console.warn('[ChunkRecovery] Chunk load failed, forcing one retry:', retryUrl.toString());
    window.location.replace(retryUrl.toString());
    return true;
  } catch {
    console.warn('[ChunkRecovery] Session storage unavailable, forcing hard reload');
    window.location.reload();
    return true;
  }
};

window.setTimeout(() => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
  } catch {
    // Ignore storage access issues
  }
}, CHUNK_RELOAD_CLEAR_DELAY_MS);

// Handle redirects for root-level routes before Router is created
const BASENAME = '/onecare';
const pathname = window.location.pathname;

// Allow root "/" to pass through (company landing page)
// Redirect other root-level routes to /onecare/ version
if (
  pathname !== '/' &&
  pathname !== '' &&
  !pathname.startsWith(BASENAME) &&
  !pathname.startsWith('/api/') &&
  !pathname.startsWith('/ws/') &&
  !pathname.startsWith('/static/') &&
  !pathname.startsWith('/media/') &&
  !pathname.startsWith('/django-admin/')
) {
  const search = window.location.search;
  const hash = window.location.hash;
  const redirectPath = `${BASENAME}${pathname}${search}${hash}`;
  window.location.replace(redirectPath);
}

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (tryRecoverFromChunkLoadError(error)) {
    event.preventDefault();
    return;
  }

  const errorMsg = error instanceof Error 
    ? error.message 
    : (error as any)?.response?.data?.error 
    || (error as any)?.message 
    || JSON.stringify(error, null, 2);
  console.error('Unhandled promise rejection:', errorMsg);
  event.preventDefault(); // Prevent default browser error logging
});

// Global error handler for uncaught errors
window.addEventListener('error', (event) => {
  const error = event.error;
  const chunkCandidate = error || {
    message: (event as ErrorEvent).message,
    filename: (event as ErrorEvent).filename,
    type: event.type,
  };
  if (tryRecoverFromChunkLoadError(chunkCandidate)) {
    event.preventDefault();
    return;
  }

  if (error) {
    const errorMsg = error instanceof Error 
      ? error.message 
      : JSON.stringify(error, null, 2);
    console.error('Uncaught error:', errorMsg);
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <App />
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);
